import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const DIFFICULTIES = [300, 600, 900];
const ROMAN_BG = "https://images.pexels.com/photos/159862/art-school-of-athens-raphael-italian-painter-fresco-159862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

/* ══════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════ */
const C = {
  gold:        "#C9A84C",
  goldLight:   "#E8C46A",
  goldDark:    "#8B6914",
  goldGlow:    "rgba(201,168,76,0.40)",
  goldBorder:  "rgba(201,168,76,0.30)",
  goldFaint:   "rgba(201,168,76,0.10)",
  burgundy:    "#5B0E14",
  burgundyLt:  "#7A1520",
  bronze:      "#4A3728",
  bronzeDk:    "#2E1E14",
  bronzeMd:    "#3D2918",
  cream:       "#F5EDD8",
  creamFade:   "rgba(245,237,216,0.55)",
  creamDim:    "rgba(245,237,216,0.28)",
  card:        "rgba(8,4,2,0.22)",
  cardHov:     "rgba(14,7,3,0.36)",
  strip:       "#0C0501",
};

/* ══════════════════════════════════════
   SCORE COUNTER
══════════════════════════════════════ */
function ScoreCounter({ value }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    if (value === prev.current) return;
    setPop(true);
    const delta = value - prev.current, steps = 12;
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplay(Math.round(prev.current + (delta * i) / steps));
      if (i >= steps) { clearInterval(iv); setDisplay(value); setPop(false); prev.current = value; }
    }, 40);
    return () => clearInterval(iv);
  }, [value]);

  return (
    <span style={{ display: "inline-block", transition: "transform 0.15s", transform: pop ? "scale(1.28)" : "scale(1)" }}>
      {display}
    </span>
  );
}

/* ══════════════════════════════════════
   CONFETTI
══════════════════════════════════════ */
function fireConfetti() {
  const colors = ["#C9A84C", "#E8C46A", "#8B6914", "#5B0E14", "#F5EDD8", "#4A3728"];
  for (let i = 0; i < 100; i++) {
    const el = document.createElement("div");
    Object.assign(el.style, {
      position: "fixed", top: "-20px", pointerEvents: "none", zIndex: "9999",
      left:   Math.random() * 100 + "vw",
      width:  (Math.random() * 10 + 5) + "px",
      height: (Math.random() * 10 + 5) + "px",
      background: colors[Math.floor(Math.random() * colors.length)],
      borderRadius: Math.random() > 0.5 ? "50%" : "3px",
      animation: `confettiFall ${Math.random() * 3 + 2}s ${Math.random()}s linear forwards`,
    });
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 5500);
  }
}

/* ══════════════════════════════════════
   PREMIUM SCORE BUTTON
   Large pill — prominent over the image
══════════════════════════════════════ */
function ScoreBtn({ catId, diff, slot, used, clicking, onClick, doubleOn }) {
  const key = `${catId}_${diff}_${slot}`;
  const isClicking = clicking === key;
  const [hov, setHov] = useState(false);

  return (
    <button
      data-testid={`tile-${catId}-${diff}-${slot}`}
      onClick={onClick}
      disabled={used || !!clicking}
      onMouseEnter={() => !used && setHov(true)}
      onMouseLeave={() => setHov(false)}
      onMouseDown={e => { if (!used) e.currentTarget.style.transform = "scale(0.93)"; }}
      onMouseUp={e   => { if (!used) e.currentTarget.style.transform = hov ? "scale(1.05)" : ""; }}
      style={{
        width:          "100%",
        padding:        "17px 0",
        background:     used
          ? "rgba(255,255,255,0.03)"
          : hov
            ? `linear-gradient(175deg, #6B4A28 0%, #3A2010 100%)`
            : `linear-gradient(175deg, ${C.bronzeMd} 0%, ${C.bronzeDk} 100%)`,
        border:         `1.5px solid ${
          used   ? "rgba(255,255,255,0.06)"
          : hov  ? C.goldLight
          : doubleOn && !used ? C.goldLight
          : C.gold
        }`,
        borderRadius:   "999px",
        color:          used
          ? "rgba(245,237,216,0.15)"
          : hov ? C.cream
          : "rgba(245,237,216,0.90)",
        fontSize:       "1.14rem",
        fontWeight:     900,
        fontFamily:     "Cairo, sans-serif",
        cursor:         used ? "not-allowed" : "pointer",
        textDecoration: used ? "line-through" : "none",
        letterSpacing:  "0.01em",
        boxShadow:      used
          ? "none"
          : hov
            ? `0 0 22px ${C.goldGlow}, 0 6px 16px rgba(0,0,0,0.50), inset 0 1px 0 rgba(201,168,76,0.28)`
            : doubleOn && !used
              ? `0 0 14px rgba(201,168,76,0.35), 0 4px 10px rgba(0,0,0,0.42), inset 0 1px 0 rgba(201,168,76,0.20)`
              : `0 4px 12px rgba(0,0,0,0.42), inset 0 1px 0 rgba(201,168,76,0.12)`,
        transition:     "all 0.20s ease",
        userSelect:     "none",
        display:        "block",
        position:       "relative",
      }}
    >
      {isClicking ? "⏳" : used ? "✓" : diff}
      {/* ×2 badge when double is on */}
      {doubleOn && !used && (
        <span style={{
          position: "absolute", top: -7, right: -6,
          background: `linear-gradient(135deg, #f5c842, #c9880c)`,
          color: "#160800", fontSize: "0.52rem", fontWeight: 900,
          padding: "2px 6px", borderRadius: 999, lineHeight: 1.4,
          pointerEvents: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.60), 0 0 6px rgba(255,180,40,0.50)",
        }}>×2</span>
      )}
    </button>
  );
}

/* ══════════════════════════════════════
   CATEGORY CARD
   Layout: [ btn col ] | [ image + name ] | [ btn col ]
   Image = 65% height, name strip fixed
══════════════════════════════════════ */
function CategoryCard({ cat, isTileUsed, clickingTile, onTileClick, doubleTeam }) {
  const [hov, setHov] = useState(false);
  const allUsed = DIFFICULTIES.every(d =>
    isTileUsed(`${cat.id}_${d}_1`) && isTileUsed(`${cat.id}_${d}_2`)
  );

  const BtnCol = ({ slot }) => (
    <div className="score-btn-col" style={{
      display:        "flex",
      flexDirection:  "column",
      justifyContent: "space-evenly",
      alignItems:     "stretch",
      width:          "82px",
      flexShrink:     0,
      gap:            "6px",
      padding:        "2px 0",
    }}>
      {DIFFICULTIES.map(d => {
        const isUsed = isTileUsed(`${cat.id}_${d}_${slot}`);
        return (
          <ScoreBtn
            key={`${cat.id}_${d}_${slot}`}
            catId={cat.id} diff={d} slot={slot}
            used={isUsed} clicking={clickingTile}
            onClick={() => onTileClick(cat.id, d, slot)}
            doubleOn={doubleTeam !== 0 && !isUsed}
          />
        );
      })}
    </div>
  );

  return (
    <div
      className="cat-card"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:        "flex",
        flexDirection:  "row",
        alignItems:     "stretch",
        gap:            "6px",
        padding:        "5px",
        borderRadius:   "12px",
        background:     hov ? C.cardHov : C.card,
        border:         `1.5px solid ${hov ? "rgba(201,168,76,0.55)" : C.goldBorder}`,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        boxShadow:      hov
          ? `0 0 0 1px rgba(201,168,76,0.16), 0 20px 56px rgba(0,0,0,0.60), inset 0 1px 0 rgba(201,168,76,0.14)`
          : `0 6px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(201,168,76,0.06)`,
        opacity:        allUsed ? 0.30 : 1,
        transform:      hov ? "translateY(-2px) scale(1.007)" : "translateY(0) scale(1)",
        transition:     "all 0.24s cubic-bezier(0.34,1.1,0.64,1)",
        overflow:       "hidden",
        position:       "relative",
      }}
    >
      {/* Top gold line */}
      <div style={{
        position: "absolute", top: 0, left: "12%", right: "12%", height: "1px",
        background: `linear-gradient(90deg, transparent, ${C.goldBorder}, transparent)`,
        pointerEvents: "none",
      }} />

      <BtnCol slot={1} />

      {/* CENTER: image (65%) + name strip (35%) */}
      <div style={{
        flex:          1,
        display:       "flex",
        flexDirection: "column",
        minWidth:      0,
        borderRadius:  "8px",
        overflow:      "hidden",
      }}>
        {/* Image — fills remaining space above name strip */}
        <div style={{
          flex:     "1 1 0",
          position: "relative",
          overflow: "hidden",
          background: "rgba(12,6,2,0.90)",
        }}>
          {cat.image_url ? (
            <img
              src={cat.image_url} alt="" aria-hidden="true"
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: "block",
                transition: "transform 0.40s ease",
                transform: hov ? "scale(1.07)" : "scale(1)",
              }}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.goldBorder, fontSize: "1.8rem",
            }}>✦</div>
          )}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.28) 100%)",
            pointerEvents: "none",
          }} />
        </div>

        {/* Name strip — slim, text-hug only */}
        <div style={{
          flexShrink:  0,
          background:  "rgba(8,3,1,0.82)",
          borderTop:   `1px solid ${C.goldBorder}`,
          display:     "flex",
          alignItems:  "center",
          justifyContent: "center",
          padding:     "5px 6px",
        }}>
          <span style={{
            color:         C.cream,
            fontFamily:    "Cairo, Tajawal, sans-serif",
            fontSize:      "clamp(0.84rem, 1.08vw, 1.16rem)",
            fontWeight:    700,
            lineHeight:    1.25,
            letterSpacing: "0.01em",
            textAlign:     "center",
            display:       "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow:      "hidden",
          }}>
            {cat.name}
          </span>
        </div>
      </div>

      <BtnCol slot={2} />
    </div>
  );
}

/* ══════════════════════════════════════
   TEAM PANEL — includes ⚡ double button
══════════════════════════════════════ */
function TeamPanel({ team, name, score, isActive, icon, reversed, onAdd, onRemove, doubleActive, onDouble, doubleUsed }) {
  const [showDoubleTip, setShowDoubleTip] = React.useState(false);
  const adjBtn = (label, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: "22px", height: "22px", borderRadius: "7px",
        background: "rgba(201,168,76,0.08)",
        border: `1px solid rgba(201,168,76,0.20)`,
        color: "rgba(201,168,76,0.55)", fontSize: "0.85rem", fontWeight: 900,
        cursor: "pointer", lineHeight: 1, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s ease",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.20)"; e.currentTarget.style.color = C.gold; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(201,168,76,0.08)"; e.currentTarget.style.color = "rgba(201,168,76,0.55)"; }}
    >
      {label}
    </button>
  );

  return (
    <div className="team-panel-wrapper" style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: reversed ? "flex-end" : "flex-start" }}>
      {/* Main panel */}
      <div
        className="team-panel-main"
        data-testid={`team${team}-score`}
        style={{
          display:        "flex",
          flexDirection:  reversed ? "row-reverse" : "row",
          alignItems:     "center",
          gap:            "10px",
          padding:        "10px 16px",
          borderRadius:   "14px",
          minWidth:       "clamp(155px, 15vw, 220px)",
          background:     isActive
            ? `linear-gradient(135deg, rgba(91,14,20,0.55) 0%, rgba(46,6,8,0.65) 100%)`
            : "rgba(8,4,2,0.68)",
          border:         `1.5px solid ${isActive ? "rgba(201,168,76,0.44)" : "rgba(201,168,76,0.16)"}`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow:      isActive
            ? `0 0 24px rgba(91,14,20,0.35), 0 6px 24px rgba(0,0,0,0.45)`
            : `0 4px 20px rgba(0,0,0,0.35)`,
          transition:     "all 0.35s ease",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, textAlign: reversed ? "right" : "left" }}>
          <div style={{
            fontSize: "0.52rem", color: "rgba(201,168,76,0.40)",
            fontWeight: 700, letterSpacing: "0.14em",
            textTransform: "uppercase", marginBottom: "2px",
            fontFamily: "Cairo, sans-serif",
          }}>TEAM</div>
          <div style={{
            fontSize:   "clamp(0.75rem, 1.0vw, 0.92rem)", fontWeight: 800,
            color:      isActive ? C.cream : C.creamFade,
            overflow:   "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontFamily: "Cairo, Tajawal, sans-serif", transition: "color 0.35s",
          }}>
            {reversed ? `${name} ${icon}` : `${icon} ${name}`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
          {onRemove && adjBtn("−", onRemove)}
          <div style={{
            fontSize:   "clamp(1.7rem, 2.4vw, 2.4rem)", fontWeight: 900,
            color:      isActive ? C.goldLight : C.gold, lineHeight: 1,
            fontFamily: "Cairo, sans-serif", minWidth: "3ch", textAlign: "center",
            textShadow: isActive ? `0 0 18px ${C.goldGlow}` : "none",
            transition: "all 0.35s",
          }}>
            <ScoreCounter value={score} />
          </div>
          {onAdd && adjBtn("+", onAdd)}
        </div>
      </div>

      {/* ⚡ Double button — per team */}
      <div className="team-double-wrap" style={{ position: "relative" }}>
        <button
          onClick={doubleUsed ? undefined : onDouble}
          onMouseEnter={() => setShowDoubleTip(true)}
          onMouseLeave={() => setShowDoubleTip(false)}
          style={{
            display:      "flex", alignItems: "center", gap: "6px",
            padding:      "7px 18px", borderRadius: "999px",
            background:   doubleUsed
              ? "rgba(255,255,255,0.04)"
              : doubleActive
              ? `linear-gradient(135deg, rgba(201,168,76,0.40), rgba(139,105,20,0.32))`
              : "rgba(8,4,2,0.72)",
            border:       `2px solid ${
              doubleUsed ? "rgba(255,255,255,0.10)"
              : doubleActive ? C.goldLight
              : "rgba(201,168,76,0.35)"
            }`,
            color:        doubleUsed ? "rgba(245,237,216,0.25)" : doubleActive ? C.goldLight : C.cream,
            fontWeight:   800, fontSize: "0.82rem",
            fontFamily:   "Cairo, sans-serif",
            cursor:       doubleUsed ? "not-allowed" : "pointer",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            boxShadow:    doubleUsed
              ? "none"
              : doubleActive
              ? `0 0 24px rgba(201,168,76,0.65), 0 0 10px rgba(255,180,60,0.35), 0 3px 10px rgba(0,0,0,0.50)`
              : `0 2px 10px rgba(0,0,0,0.35)`,
            animation:    doubleActive && !doubleUsed ? "doubleGlow 1.6s ease-in-out infinite" : "none",
            transition:   "all 0.22s ease", whiteSpace: "nowrap",
            minWidth:     "clamp(140px, 14vw, 185px)",
            justifyContent: "center",
            opacity:      doubleUsed ? 0.55 : 1,
          }}
        >
          <span style={{ fontSize: "0.85rem" }}>⚡</span>
          <span>{doubleUsed ? "✗ مستخدمة" : doubleActive ? "✓ مضاعفة مفعّلة" : "مضاعفة النقاط"}</span>
        </button>
        {/* Tooltip */}
        {showDoubleTip && (
          <div style={{
            position:   "absolute",
            bottom:     "calc(100% + 7px)",
            right:      0,
            zIndex:     999,
            background: "rgba(10,5,2,0.96)",
            border:     `1px solid ${C.goldBorder}`,
            borderRadius: "10px",
            padding:    "8px 13px",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow:  "0 6px 20px rgba(0,0,0,0.60)",
            color:      C.cream,
            fontSize:   "0.74rem",
            fontFamily: "Cairo, Tajawal, sans-serif",
            lineHeight: 1.6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}>
            {doubleUsed
              ? <span style={{ color: "rgba(245,237,216,0.45)" }}>✗ مستخدمة — مرة واحدة فقط طوال المباراة</span>
              : doubleActive
              ? <><span style={{ color: C.goldLight, fontWeight: 800 }}>✓ مفعّلة</span> — السؤال القادم يُحتسب × ٢</>
              : <>اضغط لتفعيل <span style={{ color: C.goldLight, fontWeight: 800 }}>مضاعفة النقاط</span><br />السؤال التالي يُحتسب بضعف قيمته</>
            }
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN GAME BOARD
══════════════════════════════════════ */
export default function GameBoardPage() {
  const navigate = useNavigate();
  const {
    session, resetGame, currentTurn,
    markTileUsed, isTileUsed, selectedQuestions, teamScores,
    saveSession, gameMode, tournamentState, soloSetup,
    switchTurn, updateScore, currentUser, userToken,
    lifelinesState, consumeLifeline,
    saveStatus, saveGameSnapshot, completeGameSave, manifest, saveManifest,
  } = useGame();
  const isSolo = gameMode === "solo";

  const [categories, setCategories]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showWinner, setShowWinner]         = useState(false);
  const [clickingTile, setClickingTile]     = useState(null);
  const [showHostPanel, setShowHostPanel]   = useState(false);
  // doubleTeam: 0 = off, 1 = team1 activated, 2 = team2 activated
  const [doubleTeam, setDoubleTeam]         = useState(0);
  const [showSaveExitConfirm, setShowSaveExitConfirm] = useState(false);
  const [isSavingExit, setIsSavingExit]     = useState(false);

  // ── Save ID for this session (persisted in localStorage) ──────────────────
  const saveVersionRef = useRef(parseInt(localStorage.getItem(`save_version_${session?.id}`) || "0"));
  // ── SOLO: track whether we've already shown the "target reached" toast ──
  const soloTargetToastedRef = useRef(false);

  // ── Build snapshot of current game state ──────────────────────────────────
  const buildSnapshot = useCallback((extraFields = {}) => ({
    session_id: session?.id,
    status: "paused",
    team1_name: session?.team1_name || "",
    team2_name: session?.team2_name || "",
    team1_score: teamScores.team1,
    team2_score: teamScores.team2,
    current_turn: currentTurn,
    categories: categories.map(c => ({ id: c.id, name: c.name, color: c.color, image_url: c.image_url, is_special: c.is_special, icon: c.icon || "" })),
    used_tiles: [...selectedQuestions],
    lifelines: lifelinesState,
    used_question_ids: session?.used_questions || [],
    double_team: doubleTeam,
    game_mode: gameMode,
    // SOLO fields (only sent when in solo mode)
    solo_player_name: isSolo ? (soloSetup?.playerName || session?.team1_name || "") : "",
    solo_difficulty: isSolo ? (soloSetup?.difficulty || null) : null,
    solo_target: isSolo ? (soloSetup?.target || null) : null,
    current_question_state: null,
    ...extraFields,
  }), [session, teamScores, currentTurn, categories, selectedQuestions, lifelinesState, doubleTeam, gameMode, isSolo, soloSetup]);

  // ── Auto-save: debounced 4s after any relevant state change ───────────────
  const autoSaveTimerRef = useRef(null);
  const prevSaveStateRef = useRef(null);

  const triggerAutoSave = useCallback(() => {
    if (!userToken || !session?.id || categories.length === 0) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      saveGameSnapshot(buildSnapshot());
    }, 4000);
  }, [userToken, session, categories, saveGameSnapshot, buildSnapshot]);

  useEffect(() => {
    if (!userToken || !session?.id || categories.length === 0) return;
    const key = `${teamScores.team1}|${teamScores.team2}|${currentTurn}|${JSON.stringify(lifelinesState)}|${[...selectedQuestions].sort().join(",")}`;
    if (prevSaveStateRef.current !== null && prevSaveStateRef.current !== key) {
      triggerAutoSave();
    }
    prevSaveStateRef.current = key;
  }, [teamScores, currentTurn, lifelinesState, selectedQuestions, userToken, session, categories, triggerAutoSave]);

  // ── Save & Exit handler ────────────────────────────────────────────────────
  const handleSaveAndExit = async () => {
    if (!userToken) { toast.error("يجب تسجيل الدخول لحفظ المباراة"); return; }
    setIsSavingExit(true);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const result = await saveGameSnapshot({ ...buildSnapshot(), status: "paused" });
    setIsSavingExit(false);
    setShowSaveExitConfirm(false);
    if (result?._authExpired) {
      toast.error("انتهت جلسة دخولك — سجّل الدخول مجدداً لحفظ المباراة");
      setTimeout(() => navigate("/login"), 1200);
    } else if (result) {
      toast.success("✓ تم حفظ المباراة — تقدر تكملها في أي وقت");
      setTimeout(() => navigate("/my-games"), 700);
    } else {
      toast.error("فشل الحفظ — جرب مجدداً");
    }
  };

  useEffect(() => {
    if (!session) { navigate("/"); return; }
    loadBoard();
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, []); // eslint-disable-line

  const loadBoard = async () => {
    const allIds = [...(session?.team1_categories || []), ...(session?.team2_categories || [])];
    const { data: all } = await axios.get(`${API}/categories`);
    const loaded = allIds.map(id => all.find(c => c.id === id)).filter(Boolean);
    setCategories(loaded);
    // Persist category metadata for QuestionPage auto-save
    if (session?.id) {
      localStorage.setItem(`categories_${session.id}`, JSON.stringify(
        loaded.map(c => ({ id: c.id, name: c.name, color: c.color, image_url: c.image_url, is_special: c.is_special, icon: c.icon || "" }))
      ));
    }
    // If manifest missing from context but session exists, try loading from server
    if (!manifest && session?.id) {
      try {
        const { data } = await axios.get(`${API}/game/session/${session.id}`);
        if (data.manifest) {
          saveManifest(data.manifest, session.id);
        }
      } catch {}
    }
    setLoading(false);
  };

  const refreshScores = useCallback(async () => {
    if (!session?.id) return;
    try {
      const { data } = await axios.get(`${API}/game/session/${session.id}`);
      saveSession({ ...session, team1_score: data.team1_score, team2_score: data.team2_score });
    } catch {}
  }, [session, saveSession]);

  useEffect(() => {
    const iv = setInterval(refreshScores, 12000);
    return () => clearInterval(iv);
  }, [refreshScores]);

  useEffect(() => {
    const h = () => refreshScores();
    window.addEventListener("scoreUpdated", h);
    return () => window.removeEventListener("scoreUpdated", h);
  }, [refreshScores]);

  // ── SOLO: celebrate once when target is reached mid-game ─────────────────
  useEffect(() => {
    if (!isSolo || soloTargetToastedRef.current) return;
    const target = soloSetup?.target;
    if (target && teamScores.team1 >= target) {
      soloTargetToastedRef.current = true;
      toast.success(`🎯 وصلت للهدف! ${target.toLocaleString()} نقطة — واصل!`, { duration: 4000 });
    }
  }, [isSolo, teamScores.team1, soloSetup]); // eslint-disable-line

  const handleDouble = (team) => {
    if (team !== currentTurn && !isSolo) {
      toast("مضاعفة النقاط تُفعَّل فقط أثناء دورك", { icon: "⚡" });
      return;
    }
    if (!lifelinesState[team]?.doublePoints) {
      toast("مضاعفة النقاط مستخدمة مسبقاً لهذا الفريق", { icon: "⚡" });
      return;
    }
    if (doubleTeam === team) {
      setDoubleTeam(0);
      toast("تم إلغاء مضاعفة النقاط", { icon: "⚡" });
    } else {
      setDoubleTeam(team);
      const tName = isSolo
        ? (soloSetup?.playerName || session?.team1_name)
        : (team === 1 ? session?.team1_name : session?.team2_name);
      toast.success(isSolo ? `⚡ مضاعفة النقاط مفعّلة` : `⚡ مضاعفة النقاط مفعّلة للفريق: ${tName}`);
    }
  };

  const handleTileClick = (catId, difficulty, slot) => {
    const key = `${catId}_${difficulty}_${slot}`;
    if (isTileUsed(key) || clickingTile) return;

    const wasDouble   = doubleTeam !== 0;
    const doubleForTeam = doubleTeam;

    // ── Read from pre-prepared manifest (instant, no API call) ──
    const q = manifest?.[key];
    if (!q) {
      // Manifest missing — redirect to prepare screen which re-fetches idempotently
      toast("جاري تجهيز الأسئلة...");
      navigate("/prepare");
      return;
    }

    setClickingTile(key);
    markTileUsed(key);
    setDoubleTeam(0);
    if (wasDouble) consumeLifeline(doubleForTeam, "doublePoints");

    const cat = categories.find(c => c.id === catId);
    setClickingTile(null);

    navigate("/question", {
      state: {
        question: q, catId, difficulty, slot,
        catName: cat?.name,
        catIsSpecial: !!(cat?.is_special),
        turnTeam: currentTurn,
        doubleActive: wasDouble,
      },
    });
  };

  const handleEndGame = () => {
    fireConfetti();
    setShowEndConfirm(false);
    setShowWinner(true);
    // Mark save as completed so it won't appear in active saves list
    const saveId = localStorage.getItem(`save_id_${session?.id}`);
    if (saveId) {
      const completeBody = { final_team1_score: teamScores.team1, final_team2_score: teamScores.team2 };
      if (isSolo && soloSetup?.difficulty) {
        completeBody.solo_difficulty = soloSetup.difficulty;
        completeBody.solo_final_score = teamScores.team1;
      }
      completeGameSave(saveId, completeBody);
    }
  };

  /* ── Loading ── */
  if (loading) return (
    <div style={{
      height: "100svh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#060302",
    }}>
      <div style={{
        color: C.cream, fontFamily: "Cairo, sans-serif", fontSize: "1rem", fontWeight: 700,
        padding: "14px 28px", borderRadius: "12px",
        background: C.card, border: `1.5px solid ${C.goldBorder}`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        animation: "loadPulse 1.5s ease-in-out infinite",
      }}>
        جاري تحميل اللوحة...
      </div>
    </div>
  );

  const allUsed = categories.every(c =>
    DIFFICULTIES.every(d => isTileUsed(`${c.id}_${d}_1`) && isTileUsed(`${c.id}_${d}_2`))
  );
  const winner = allUsed || showWinner
    ? teamScores.team1 > teamScores.team2 ? session?.team1_name
      : teamScores.team2 > teamScores.team1 ? session?.team2_name : "تعادل"
    : null;
  // SOLO: did the player beat the target?
  const soloWon = isSolo && teamScores.team1 >= (soloSetup?.target || Infinity);

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div className="board-page-root" style={{
      minHeight:     "100svh",
      display:       "flex",
      flexDirection: "column",
      padding:       "6px 10px",
      gap:           "5px",
      position:      "relative",
      overflow:      "hidden",
    }}>

      {/* ── GLOBAL CSS ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; overflow-x: hidden; background: #060302; }

        @keyframes turnPulse {
          0%,100% { opacity:1; }
          50%      { opacity:0.65; }
        }
        @keyframes doubleGlow {
          0%,100% { box-shadow: 0 0 16px rgba(201,168,76,0.45), 0 2px 8px rgba(0,0,0,0.40); }
          50%      { box-shadow: 0 0 28px rgba(201,168,76,0.75), 0 2px 8px rgba(0,0,0,0.40); }
        }
        @keyframes loadPulse {
          0%,100% { opacity:1; }
          50%      { opacity:0.40; }
        }
        @keyframes confettiFall {
          to { transform:translateY(110vh) rotate(720deg); opacity:0; }
        }
        @keyframes winnerGlow {
          0%,100% { text-shadow: 0 0 30px rgba(201,168,76,0.60), 0 2px 12px rgba(0,0,0,0.70); }
          50%      { text-shadow: 0 0 70px rgba(201,168,76,0.90), 0 2px 12px rgba(0,0,0,0.70); }
        }
        @keyframes winnerFadeIn {
          from { opacity:0; transform:scale(0.88) translateY(18px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
      `}</style>

      {/* ── ROMAN BACKGROUND — 30% clearer ── */}
      <div style={{
        position:   "fixed", inset: 0, zIndex: 0,
        backgroundImage: `url("${ROMAN_BG}")`,
        backgroundSize: "cover", backgroundPosition: "center",
        filter:     "blur(4px) brightness(0.62) saturate(0.82)",
        transform:  "scale(1.05)",
      }} />
      {/* Vignette */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 1,
        background: "radial-gradient(ellipse at 50% 40%, rgba(12,6,3,0.10) 0%, rgba(4,2,1,0.44) 100%)",
      }} />

      {/* ══════════════════════════════
          HEADER
      ══════════════════════════════ */}
      <div className="board-header" style={{
        position:            "relative", zIndex: 10,
        display:             "grid",
        gridTemplateColumns: "1fr auto 1fr",
        gap:                 "6px",
        alignItems:          "center",
        flexShrink:          0,
      }}>

        {/* Team 1 / SOLO player panel */}
        <div className="board-team-1" style={{ display: "flex", justifyContent: "flex-start" }}>
          {isSolo ? (
            /* SOLO: player name + score + Double Points button */
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{
                display: "flex", flexDirection: "column", gap: "3px",
                padding: "8px 14px", borderRadius: "14px",
                background: "rgba(10,20,60,0.65)",
                border: "1.5px solid rgba(147,197,253,0.30)",
                backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                minWidth: "clamp(120px,14vw,180px)",
              }}>
                <span style={{ fontSize: "0.60rem", fontWeight: 700, color: "rgba(147,197,253,0.65)", letterSpacing: "0.14em" }}>🎮 {soloSetup?.playerName || session?.team1_name}</span>
                <span style={{ fontSize: "clamp(1.2rem,2.5vw,1.8rem)", fontWeight: 900, color: C.goldLight, textShadow: `0 0 16px ${C.goldGlow}` }}>{teamScores.team1.toLocaleString()}</span>
              </div>
              {/* ⚡ Double Points — SOLO */}
              {(() => {
                const dpUsed = !lifelinesState[1]?.doublePoints;
                const dpActive = doubleTeam === 1;
                return (
                  <button
                    onClick={dpUsed ? undefined : () => handleDouble(1)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
                      padding: "5px 14px", borderRadius: "999px",
                      background: dpUsed ? "rgba(255,255,255,0.04)" : dpActive ? `linear-gradient(135deg,rgba(201,168,76,0.40),rgba(139,105,20,0.32))` : "rgba(8,4,2,0.72)",
                      border: `2px solid ${dpUsed ? "rgba(255,255,255,0.10)" : dpActive ? C.goldLight : "rgba(201,168,76,0.35)"}`,
                      color: dpUsed ? "rgba(245,237,216,0.25)" : dpActive ? C.goldLight : C.cream,
                      fontWeight: 800, fontSize: "0.75rem", fontFamily: "Cairo, sans-serif",
                      cursor: dpUsed ? "not-allowed" : "pointer",
                      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                      boxShadow: dpUsed ? "none" : dpActive ? `0 0 20px rgba(201,168,76,0.55)` : `0 2px 8px rgba(0,0,0,0.35)`,
                      animation: dpActive && !dpUsed ? "doubleGlow 1.6s ease-in-out infinite" : "none",
                      transition: "all 0.22s ease", whiteSpace: "nowrap",
                      opacity: dpUsed ? 0.55 : 1,
                    }}
                  >
                    <span>⚡</span>
                    <span>{dpUsed ? "✗ مستخدمة" : dpActive ? "✓ مفعّلة" : "مضاعفة النقاط"}</span>
                  </button>
                );
              })()}
            </div>
          ) : (
            <TeamPanel
              team={1} name={session?.team1_name}
              score={teamScores.team1} isActive={currentTurn === 1}
              icon="🦁" reversed={false}
              onAdd={() => updateScore(1, 100)}
              onRemove={() => updateScore(1, -100)}
              doubleActive={doubleTeam === 1}
              onDouble={() => handleDouble(1)}
              doubleUsed={!lifelinesState[1]?.doublePoints}
            />
          )}
        </div>

        {/* Center */}
        <div className="board-center-col" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
          {/* Logo */}
          <div style={{
            fontFamily:    "Cairo, sans-serif",
            fontWeight:    900,
            fontSize:      "clamp(1.4rem, 3.0vw, 2.1rem)",
            letterSpacing: "0.04em",
            lineHeight:    1.1,
            background:    `linear-gradient(135deg, ${C.goldLight} 0%, ${C.gold} 50%, ${C.goldDark} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>حُجّة</div>

          {/* Gold rule */}
          <div style={{
            width: "100%", height: "1px",
            background: `linear-gradient(90deg, transparent, ${C.goldBorder}, transparent)`,
          }} />

          {/* Turn indicator (standard/tournament) OR SOLO target progress */}
          {isSolo ? (
            /* SOLO target progress */
            (() => {
              const target = soloSetup?.target || 8000;
              const score = teamScores.team1;
              const pct = Math.min(100, Math.round((score / target) * 100));
              const reached = score >= target;
              const diffLabel = soloSetup?.difficulty === "easy" ? "سهل" : soloSetup?.difficulty === "hard" ? "صعب" : "متوسط";
              return (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
                  borderRadius: "12px", padding: "8px 18px",
                  background: reached ? "rgba(20,70,30,0.75)" : "rgba(20,30,70,0.75)",
                  border: `2px solid ${reached ? "rgba(74,222,128,0.60)" : "rgba(147,197,253,0.45)"}`,
                  color: reached ? "#86efac" : "rgba(147,197,253,0.9)",
                  fontFamily: "Cairo, sans-serif",
                  backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                  minWidth: "clamp(150px, 16vw, 220px)",
                }}>
                  <span style={{ fontSize: "0.60rem", fontWeight: 700, opacity: 0.75, letterSpacing: "0.14em" }}>
                    {reached ? "✓ الهدف محقّق!" : `SOLO — ${diffLabel}`}
                  </span>
                  <span style={{ fontSize: "clamp(0.78rem,1.1vw,0.95rem)", fontWeight: 900 }}>
                    {score.toLocaleString()} / {target.toLocaleString()}
                  </span>
                  {/* Progress bar */}
                  <div style={{ width: "100%", height: "5px", borderRadius: "99px", background: "rgba(255,255,255,0.10)", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: "99px",
                      width: `${pct}%`,
                      background: reached ? "linear-gradient(90deg,#4ade80,#22c55e)" : "linear-gradient(90deg,#60a5fa,#93c5fd)",
                      transition: "width 0.6s cubic-bezier(0.34,1.2,0.64,1)",
                    }} />
                  </div>
                </div>
              );
            })()
          ) : (
            <div
              data-testid="turn-indicator"
              onClick={switchTurn}
              title="اضغط لتبديل الدور"
              style={{
                display:        "flex", flexDirection: "column", alignItems: "center", gap: "2px",
                borderRadius:   "12px", padding:  "8px 30px",
                background:     currentTurn === 1
                  ? `linear-gradient(135deg, rgba(91,14,20,0.70) 0%, rgba(46,6,8,0.80) 100%)`
                  : `linear-gradient(135deg, rgba(46,30,8,0.70) 0%, rgba(20,12,3,0.80) 100%)`,
                border:         `2px solid ${currentTurn === 1 ? "rgba(201,168,76,0.80)" : "rgba(201,140,60,0.70)"}`,
                color:          currentTurn === 1 ? C.goldLight : "#E8B46A",
                fontFamily:     "Cairo, sans-serif",
                animation:      "turnPulse 2.5s ease-in-out infinite",
                whiteSpace:     "nowrap", cursor: "pointer", userSelect: "none",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow:      currentTurn === 1
                  ? `0 0 20px rgba(91,14,20,0.50), 0 4px 16px rgba(0,0,0,0.50)`
                  : `0 0 20px rgba(100,70,10,0.40), 0 4px 16px rgba(0,0,0,0.50)`,
                transition:     "all 0.35s ease",
                minWidth:       "clamp(150px, 16vw, 210px)",
              }}
            >
              <span style={{ fontSize: "0.60rem", fontWeight: 700, opacity: 0.65, letterSpacing: "0.14em", textTransform: "uppercase" }}>الدور الحالي</span>
              <span style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "clamp(0.88rem, 1.25vw, 1.08rem)", fontWeight: 900 }}>
                <span>{currentTurn === 1 ? "🦁" : "🦅"}</span>
                <span>{currentTurn === 1 ? session?.team1_name : session?.team2_name}</span>
              </span>
            </div>
          )}

          {/* End game + Settings */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", position: "relative" }}>
            {/* Save status indicator */}
            {userToken && saveStatus !== "idle" && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                background: saveStatus === "saved" ? "rgba(30,80,40,0.90)" : saveStatus === "saving" ? "rgba(40,30,8,0.90)" : "rgba(80,20,20,0.90)",
                border: `1px solid ${saveStatus === "saved" ? "rgba(100,200,100,0.35)" : saveStatus === "saving" ? "rgba(201,168,76,0.30)" : "rgba(200,60,60,0.35)"}`,
                borderRadius: "999px", padding: "3px 10px", whiteSpace: "nowrap",
                color: saveStatus === "saved" ? "#86efac" : saveStatus === "saving" ? C.gold : "#fca5a5",
                fontSize: "0.62rem", fontWeight: 700, fontFamily: "Cairo, sans-serif",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)", zIndex: 50,
              }}>
                {saveStatus === "saving" ? "⏳ جاري الحفظ..." : saveStatus === "saved" ? "✓ تم الحفظ" : "⚠ فشل الحفظ"}
              </div>
            )}
            {userToken && (
              <button
                onClick={() => setShowSaveExitConfirm(true)}
                style={{
                  padding:      "5px 12px", borderRadius: "999px",
                  background:   "rgba(8,30,16,0.65)",
                  border:       "1px solid rgba(50,100,60,0.60)",
                  color:        "rgba(134,239,172,0.75)",
                  fontWeight:   700, fontSize: "0.70rem",
                  fontFamily:   "Cairo, sans-serif", cursor: "pointer",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  transition:   "all 0.18s ease", whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(20,70,35,0.85)"; e.currentTarget.style.color = "#86efac"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(8,30,16,0.65)"; e.currentTarget.style.color = "rgba(134,239,172,0.75)"; }}
              >
                💾 حفظ والخروج
              </button>
            )}
            <button
              data-testid="end-game-btn"
              onClick={() => setShowEndConfirm(true)}
              style={{
                padding:      "5px 14px", borderRadius: "999px",
                background:   "rgba(60,8,12,0.65)",
                border:       "1px solid rgba(91,14,20,0.70)",
                color:        "rgba(245,237,216,0.72)",
                fontWeight:   700, fontSize: "0.70rem",
                fontFamily:   "Cairo, sans-serif", cursor: "pointer",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                transition:   "all 0.18s ease", whiteSpace: "nowrap",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,14,20,0.82)"; e.currentTarget.style.color = C.cream; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(60,8,12,0.65)"; e.currentTarget.style.color = "rgba(245,237,216,0.72)"; }}
            >
              إنهاء اللعبة
            </button>
            {/* Settings gear */}
            <button
              onClick={() => setShowHostPanel(p => !p)}
              title="تحكم المضيف"
              style={{
                width: "32px", height: "32px", borderRadius: "9px",
                background: showHostPanel ? `linear-gradient(135deg, ${C.goldLight}, ${C.gold})` : "rgba(6,3,1,0.80)",
                border: `1.5px solid ${showHostPanel ? C.gold : C.goldBorder}`,
                color: showHostPanel ? C.bronzeDk : C.creamFade,
                fontSize: "0.88rem", cursor: "pointer", backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                transition: "all 0.18s ease",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >⚙️</button>
            {/* Host panel dropdown */}
            {showHostPanel && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: "210px", zIndex: 60,
                background: "rgba(8,4,2,0.95)", border: `1.5px solid ${C.goldBorder}`,
                borderRadius: "14px", backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                padding: "12px 14px", display: "flex", flexDirection: "column", gap: "8px",
                boxShadow: `0 16px 48px rgba(0,0,0,0.65)`,
              }}>
                <div style={{
                  color: "rgba(201,168,76,0.55)", fontFamily: "Cairo,sans-serif",
                  fontWeight: 700, fontSize: "0.70rem", letterSpacing: "0.10em",
                  textAlign: "center", borderBottom: `1px solid rgba(201,168,76,0.10)`,
                  paddingBottom: "8px",
                }}>تحكم المضيف</div>
                <button
                  onClick={switchTurn}
                  style={{
                    padding: "8px 12px", borderRadius: "10px", cursor: "pointer",
                    background: "rgba(201,168,76,0.10)", border: `1px solid rgba(201,168,76,0.22)`,
                    color: C.creamFade, fontFamily: "Cairo,sans-serif",
                    fontWeight: 700, fontSize: "0.78rem", transition: "all 0.15s ease",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.20)"; e.currentTarget.style.color = C.cream; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(201,168,76,0.10)"; e.currentTarget.style.color = C.creamFade; }}
                >🔄 تبديل الدور</button>
                {[
                  { team: 1, name: session?.team1_name, icon: "🦁", color: "rgba(255,200,170,0.85)", border: "rgba(200,100,60,0.30)", bg: "rgba(120,40,10,0.22)" },
                  { team: 2, name: session?.team2_name, icon: "🦅", color: "rgba(245,220,170,0.85)", border: "rgba(201,168,76,0.28)", bg: "rgba(80,50,10,0.22)" },
                ].map(({ team, name, icon, color, border, bg }) => (
                  <div key={team} style={{ borderRadius: "10px", padding: "8px 10px", background: bg, border: `1px solid ${border}` }}>
                    <div style={{ color, fontFamily: "Cairo,sans-serif", fontWeight: 700, fontSize: "0.70rem", marginBottom: "6px", textAlign: "center" }}>
                      {icon} {name}
                    </div>
                    <div style={{ display: "flex", gap: "5px" }}>
                      {[300, 600, 900].map(pts => (
                        <div key={pts} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                          <button
                            onClick={() => updateScore(team, pts)}
                            style={{
                              padding: "4px 2px", borderRadius: "6px", cursor: "pointer", fontSize: "0.60rem",
                              fontFamily: "Cairo,sans-serif", fontWeight: 800,
                              background: "rgba(67,233,123,0.10)", border: "1px solid rgba(67,233,123,0.28)",
                              color: "#43e97b", transition: "all 0.12s ease",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(67,233,123,0.22)"}
                            onMouseLeave={e => e.currentTarget.style.background = "rgba(67,233,123,0.10)"}
                          >+{pts}</button>
                          <button
                            onClick={() => updateScore(team, -pts)}
                            style={{
                              padding: "4px 2px", borderRadius: "6px", cursor: "pointer", fontSize: "0.60rem",
                              fontFamily: "Cairo,sans-serif", fontWeight: 800,
                              background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.26)",
                              color: "#f87171", transition: "all 0.12s ease",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.22)"}
                            onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.10)"}
                          >-{pts}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Team 2 — hidden in SOLO */}
        <div className="board-team-2" style={{ display: "flex", justifyContent: "flex-end" }}>
          {!isSolo && (
            <TeamPanel
              team={2} name={session?.team2_name}
              score={teamScores.team2} isActive={currentTurn === 2}
              icon="🦅" reversed={true}
              onAdd={() => updateScore(2, 100)}
              onRemove={() => updateScore(2, -100)}
              doubleActive={doubleTeam === 2}
              onDouble={() => handleDouble(2)}
              doubleUsed={!lifelinesState[2]?.doublePoints}
            />
          )}
        </div>
      </div>

      {/* ══════════════════════════════
          CATEGORY GRID — 3 × 2
      ══════════════════════════════ */}
      <div className="board-cats-grid" style={{
        position:            "relative", zIndex: 10,
        flex:                1,
        minHeight:           0,
        display:             "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows:    "repeat(2, 1fr)",
        gap:                 "6px",
      }}>
        {categories.slice(0, 6).map(cat => (
          <CategoryCard
            key={cat.id}
            cat={cat}
            isTileUsed={isTileUsed}
            clickingTile={clickingTile}
            onTileClick={handleTileClick}
            doubleTeam={doubleTeam}
          />
        ))}
      </div>

      {/* ══════════════════════════════
          ALL-USED BANNER
      ══════════════════════════════ */}
      {allUsed && !showWinner && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40,
          display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
          padding: "18px 24px",
          background: "rgba(6,3,1,0.93)", backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderTop: `1.5px solid ${C.goldBorder}`,
          boxShadow: `0 -8px 32px rgba(0,0,0,0.55)`,
        }}>
          <div style={{ color: C.goldLight, fontFamily: "Cairo,sans-serif", fontWeight: 900, fontSize: "1.2rem", textShadow: `0 0 20px ${C.goldGlow}` }}>
            {isSolo
              ? (soloWon ? `🎯 وصلت للهدف! ${teamScores.team1.toLocaleString()} نقطة` : `انتهت الأسئلة — ${teamScores.team1.toLocaleString()} نقطة`)
              : (winner === "تعادل" ? "🤝 تعادل!" : `🏆 ${winner} فاز!`)
            }
          </div>
          <button
            onClick={() => {
              fireConfetti();
              setShowWinner(true);
              const saveId = localStorage.getItem(`save_id_${session?.id}`);
              if (saveId) {
                const completeBody = { final_team1_score: teamScores.team1, final_team2_score: teamScores.team2 };
                if (isSolo && soloSetup?.difficulty) {
                  completeBody.solo_difficulty = soloSetup.difficulty;
                  completeBody.solo_final_score = teamScores.team1;
                }
                completeGameSave(saveId, completeBody);
              }
            }}
            style={{
              padding: "12px 40px", borderRadius: "999px", fontWeight: 900, fontSize: "0.95rem",
              background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
              color: C.bronzeDk, border: "none", cursor: "pointer", fontFamily: "Cairo,sans-serif",
              boxShadow: `0 6px 28px ${C.goldGlow}`, transition: "all 0.18s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.10)"; e.currentTarget.style.transform = "scale(1.03)"; }}
            onMouseLeave={e => { e.currentTarget.style.filter = ""; e.currentTarget.style.transform = ""; }}
          >
            عرض النتيجة النهائية
          </button>
        </div>
      )}

      {/* ══════════════════════════════
          END CONFIRM MODAL
      ══════════════════════════════ */}
      {showEndConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
          background: "rgba(4,2,1,0.84)", backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}>
          <div style={{
            borderRadius: "20px", padding: "36px 40px",
            maxWidth: "380px", width: "100%", textAlign: "center",
            background: "rgba(10,5,2,0.94)",
            border: `1.5px solid ${C.goldBorder}`,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: `0 0 0 1px rgba(201,168,76,0.08), 0 32px 80px rgba(0,0,0,0.65)`,
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🎮</div>
            <div style={{ fontSize: "1.2rem", fontWeight: 900, color: C.cream, fontFamily: "Cairo,sans-serif", marginBottom: "6px" }}>
              إنهاء اللعبة؟
            </div>
            <div style={{ fontSize: "0.84rem", color: C.creamFade, marginBottom: "26px" }}>
              سيتم إعلان الفائز الحالي
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={handleEndGame}
                style={{
                  padding: "11px 28px", borderRadius: "999px", fontWeight: 800, cursor: "pointer",
                  background: `linear-gradient(135deg, ${C.burgundyLt}, ${C.burgundy})`,
                  color: C.cream, border: `1.5px solid rgba(201,168,76,0.28)`,
                  fontFamily: "Cairo,sans-serif",
                  boxShadow: "0 4px 20px rgba(91,14,20,0.50)", transition: "all 0.18s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.15)"}
                onMouseLeave={e => e.currentTarget.style.filter = ""}
              >نعم، إنهاء</button>
              <button
                onClick={() => setShowEndConfirm(false)}
                style={{
                  padding: "11px 28px", borderRadius: "999px", fontWeight: 700, cursor: "pointer",
                  background: "rgba(255,255,255,0.05)", border: `1.5px solid rgba(255,255,255,0.10)`,
                  color: C.creamFade, fontFamily: "Cairo,sans-serif", transition: "all 0.18s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.color = C.cream; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"; e.currentTarget.style.color = C.creamFade; }}
              >رجوع</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
          SAVE & EXIT CONFIRM MODAL
      ══════════════════════════════ */}
      {showSaveExitConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
          background: "rgba(4,2,1,0.84)", backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}>
          <div style={{
            borderRadius: "20px", padding: "36px 40px",
            maxWidth: "380px", width: "100%", textAlign: "center",
            background: "rgba(10,5,2,0.94)",
            border: `1.5px solid ${C.goldBorder}`,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: `0 0 0 1px rgba(201,168,76,0.08), 0 32px 80px rgba(0,0,0,0.65)`,
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>💾</div>
            <div style={{ fontSize: "1.2rem", fontWeight: 900, color: C.cream, fontFamily: "Cairo,sans-serif", marginBottom: "6px" }}>
              حفظ المباراة والخروج؟
            </div>
            <div style={{ fontSize: "0.84rem", color: C.creamFade, marginBottom: "26px", lineHeight: 1.6 }}>
              سيتم حفظ المباراة عند نفس النقطة.<br />
              تقدر تكملها في أي وقت من «مبارياتي».
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={handleSaveAndExit}
                disabled={isSavingExit}
                style={{
                  padding: "11px 28px", borderRadius: "999px", fontWeight: 800, cursor: isSavingExit ? "wait" : "pointer",
                  background: isSavingExit ? "rgba(30,70,40,0.60)" : "linear-gradient(135deg, rgba(30,80,45,0.90), rgba(15,50,25,0.90))",
                  color: "#86efac", border: `1.5px solid rgba(100,200,120,0.40)`,
                  fontFamily: "Cairo,sans-serif",
                  boxShadow: "0 4px 20px rgba(20,80,40,0.40)", transition: "all 0.18s ease",
                  opacity: isSavingExit ? 0.70 : 1,
                }}
                onMouseEnter={e => !isSavingExit && (e.currentTarget.style.filter = "brightness(1.15)")}
                onMouseLeave={e => (e.currentTarget.style.filter = "")}
              >
                {isSavingExit ? "⏳ جاري الحفظ..." : "✓ حفظ والخروج"}
              </button>
              <button
                onClick={() => setShowSaveExitConfirm(false)}
                style={{
                  padding: "11px 28px", borderRadius: "999px", fontWeight: 700, cursor: "pointer",
                  background: "rgba(255,255,255,0.05)", border: `1.5px solid rgba(255,255,255,0.10)`,
                  color: C.creamFade, fontFamily: "Cairo,sans-serif", transition: "all 0.18s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.color = C.cream; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"; e.currentTarget.style.color = C.creamFade; }}
              >رجوع</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
          WINNER SCREEN
      ══════════════════════════════ */}
      {showWinner && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "24px", textAlign: "center",
        }}>
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: `url("${ROMAN_BG}")`,
            backgroundSize: "cover", backgroundPosition: "center",
            filter: "blur(8px) brightness(0.22) saturate(0.50)",
            transform: "scale(1.05)",
          }} />
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(ellipse at center, rgba(10,5,2,0.50) 0%, rgba(4,2,1,0.88) 100%)",
          }} />
          <div style={{
            position: "relative", zIndex: 1,
            animation: "winnerFadeIn 0.52s cubic-bezier(0.34,1.2,0.64,1) both",
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
            {isSolo ? (
              /* ── SOLO result screen ── */
              <>
                <div style={{ fontSize: "clamp(3.5rem,7vw,6rem)", marginBottom: "12px", filter: `drop-shadow(0 0 28px ${soloWon ? "rgba(74,222,128,0.5)" : C.goldGlow})` }}>
                  {soloWon ? "🎯" : "💪"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                  <div style={{ height: "1px", width: "56px", background: `linear-gradient(90deg, transparent, ${C.goldBorder})` }} />
                  <span style={{ color: soloWon ? "rgba(74,222,128,0.75)" : "rgba(201,168,76,0.65)", fontFamily: "Cairo,sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.18em" }}>
                    {soloWon ? "تهانينا!" : "نتيجة التحدي"}
                  </span>
                  <div style={{ height: "1px", width: "56px", background: `linear-gradient(90deg, ${C.goldBorder}, transparent)` }} />
                </div>
                <div style={{
                  fontSize: "clamp(2rem,4vw,3.5rem)", fontWeight: 900,
                  color: soloWon ? "#4ade80" : C.cream,
                  fontFamily: "Cairo,sans-serif", marginBottom: "28px",
                  animation: "winnerGlow 2.5s ease-in-out infinite",
                }}>
                  {soloWon ? "وصلت للهدف!" : "لم تصل للهدف بعد"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "28px" }}>
                  <div style={{
                    textAlign: "center", borderRadius: "16px", padding: "18px 30px",
                    background: "rgba(8,4,2,0.82)", border: `1.5px solid ${soloWon ? "rgba(74,222,128,0.40)" : C.goldBorder}`,
                    backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", boxShadow: `0 8px 36px rgba(0,0,0,0.55)`,
                  }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: C.creamFade, fontFamily: "Cairo,sans-serif", marginBottom: "6px" }}>
                      🎮 {soloSetup?.playerName || session?.team1_name}
                    </div>
                    <div style={{ fontSize: "2.2rem", fontWeight: 900, color: soloWon ? "#4ade80" : C.goldLight, textShadow: `0 0 20px ${soloWon ? "rgba(74,222,128,0.4)" : C.goldGlow}` }}>
                      {teamScores.team1.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: C.creamFade, marginTop: "4px" }}>
                      الهدف: {(soloSetup?.target || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* ── Standard/Tournament result screen ── */
              <>
                <div style={{ fontSize: "clamp(3.5rem,7vw,6rem)", marginBottom: "12px", filter: `drop-shadow(0 0 28px ${C.goldGlow})` }}>🏆</div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                  <div style={{ height: "1px", width: "56px", background: `linear-gradient(90deg, transparent, ${C.goldBorder})` }} />
                  <span style={{ color: "rgba(201,168,76,0.65)", fontFamily: "Cairo,sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.18em" }}>الفريق الفائز</span>
                  <div style={{ height: "1px", width: "56px", background: `linear-gradient(90deg, ${C.goldBorder}, transparent)` }} />
                </div>
                <div style={{
                  fontSize: "clamp(2.6rem,5.5vw,5rem)", fontWeight: 900, color: C.cream,
                  fontFamily: "Cairo,sans-serif", marginBottom: "40px",
                  animation: "winnerGlow 2.5s ease-in-out infinite",
                }}>
                  {winner === "تعادل" ? "🤝 تعادل!" : winner}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "22px", marginBottom: "44px" }}>
                  {[
                    { name: session?.team1_name, icon: "🦁", score: teamScores.team1 },
                    { name: session?.team2_name, icon: "🦅", score: teamScores.team2, reverse: true },
                  ].map((t, i) => (
                    <React.Fragment key={i}>
                      {i === 1 && <div style={{ color: "rgba(201,168,76,0.50)", fontSize: "1.2rem", fontWeight: 900 }}>VS</div>}
                      <div style={{
                        textAlign: "center", borderRadius: "16px", padding: "18px 30px",
                        background: "rgba(8,4,2,0.82)", border: `1.5px solid ${C.goldBorder}`,
                        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", boxShadow: `0 8px 36px rgba(0,0,0,0.55)`,
                      }}>
                        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: C.creamFade, fontFamily: "Cairo,sans-serif", marginBottom: "6px" }}>
                          {t.reverse ? `${t.name} ${t.icon}` : `${t.icon} ${t.name}`}
                        </div>
                        <div style={{ fontSize: "2.2rem", fontWeight: 900, color: C.goldLight, textShadow: `0 0 20px ${C.goldGlow}` }}>
                          {t.score}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
            <button
              onClick={() => {
                if (gameMode === "tournament") {
                  const ref = tournamentState?.currentMatchRef;
                  if (ref) {
                    const winnerId = teamScores.team1 >= teamScores.team2 ? ref.team1Id : ref.team2Id;
                    navigate("/tournament/bracket", { state: { autoRecord: { roundIdx: ref.roundIdx, matchIdx: ref.matchIdx, winnerId } } });
                  } else navigate("/tournament/bracket");
                } else { resetGame(); navigate("/"); }
              }}
              style={{
                padding: "16px 56px", borderRadius: "999px", fontWeight: 900, fontSize: "1rem",
                background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
                color: C.bronzeDk, border: "none", fontFamily: "Cairo,sans-serif",
                cursor: "pointer",
                boxShadow: `0 0 0 1px ${C.goldDark}, 0 10px 40px ${C.goldGlow}`,
                transition: "all 0.18s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.filter = "brightness(1.10)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.filter = ""; }}
            >
              {gameMode === "tournament" ? "🏆 العودة للبطولة" : "🎮 لعبة جديدة"}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
